/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import _ from 'lodash';
import React, { Fragment } from 'react';
import { Box, Divider} from '@mui/material';
import { LuiMaterialTable, LuiPagination } from '@jda/lui-common-component-library-mui5';

import { useStyles } from './styles';
import { ICustomTableWithPaginatorProps } from '../../models/Common/Pagination';

const CustomTablePagination = <T extends object>({
  rowsPerPageOptions,
  labelRowsPerPage,
  data,
  keyAccessor = 'id',
  onChangePageOptions,
  currentPageNumber,
  currentItemsPerPage,
  totalCount,
  additionalContent,
  withoutPagination = false,
  selectedIds,
  onSelectedIdsChange,
  selectedRowsData,
  onSelectedRowsDataChange,
  startsFromZero,
  maxTableBodyHeight,
  renderTopToolbar = undefined,
  renderEmptyState = undefined,
  ...otherProps
}: ICustomTableWithPaginatorProps<T>) => {
  const { classes } = useStyles();

  const handleChangePage = (_event: any, newPage: number) => {
    onChangePageOptions && onChangePageOptions(currentItemsPerPage, newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    onChangePageOptions && onChangePageOptions(+event.target.value, 1);
  };

  const handleSelectionChange = (selection: { selected: string[]; selectedRowsData: T[] }) => {
    if (onSelectedIdsChange) {
      onSelectedIdsChange(selection.selected);
    }

    if (onSelectedRowsDataChange) {
      const selectedSet = new Set(selection.selected);
      const withSelectedAdded = _.unionBy(
        selectedRowsData,
        selection.selectedRowsData,
        rowData => rowData[keyAccessor]
      );
      const newSelectedRowsData = withSelectedAdded.filter(rowData => selectedSet.has(rowData[keyAccessor]));
      onSelectedRowsDataChange(newSelectedRowsData);
    }
  };

  return (
    <Fragment>
      <LuiMaterialTable
        data={data}
        paging={false}
        isHeaderSticky={true}
        keyAccessor={keyAccessor}
        enableBottomToolbar={false}
        headerStyle={'standard'}
        enableColumnActions={false}
        enableFullScreenToggle={true}
        enableDensityToggle={true}
        enableColumnsToggle={true}
        enableAlternateRowStyle={true}
        maxTableBodyHeight={maxTableBodyHeight}
        renderTopToolbar={renderTopToolbar}
        renderTopToolbarWithStandardToolbar={false}
        enableRowSelection={Boolean(selectedIds || selectedRowsData)}
        selectedIds={selectedIds || selectedRowsData?.map(rowData => rowData[keyAccessor])}
        onSelectionChange={handleSelectionChange}
        customComponents={{
          renderTopToolbarCustomActions: () => <div></div>
        }}
        renderEmptyRowsFallback={renderEmptyState}
        {...otherProps}
      />
      <Divider />
      <Box className={classes.bottomPanel}>
        {additionalContent}
        {!withoutPagination && (
          <LuiPagination
            rowsPerPage={currentItemsPerPage}
            onPageChange={handleChangePage}
            totalRows={totalCount || data.length}
            rowsPerPageOptions={rowsPerPageOptions}
            onRowsPerPageChange={handleChangeRowsPerPage}
            page={startsFromZero ? currentPageNumber : currentPageNumber - 1}
            sx ={{ padding: 0 }}
          />
        )}
      </Box>
    </Fragment>
  );
};

export default CustomTablePagination;
